import {
  MeshPhongMaterial,
  MeshPhysicalMaterial,
  type MeshPhysicalMaterialParameters,
  PointsMaterial,
  Texture,
} from 'three';

export function buildToothMaterial(
  options: MeshPhysicalMaterialParameters = {}
): MeshPhysicalMaterial {
  function getValue<K extends keyof MeshPhysicalMaterialParameters>(
    key: K,
    defaultVal: MeshPhysicalMaterialParameters[K]
  ) {
    return options.hasOwnProperty(key) ? options[key] : defaultVal;
  }

  //Default to shiny white material
  return new MeshPhysicalMaterial({
    color: getValue('color', 0xffffff),
    emissive: getValue('emissive', 0x000000),
    roughness: getValue('roughness', 0.6),
    metalness: getValue('metalness', 0),
    ior: getValue('ior', 2.33),
    reflectivity: getValue('reflectivity', 0.75),
    iridescence: getValue('iridescence', 0),
    sheen: getValue('sheen', 0.7),
    sheenRoughness: getValue('sheenRoughness', 1),
    sheenColor: getValue('sheenColor', 0xffffff),
    clearcoat: getValue('clearcoat', 0.6),
    clearcoatRoughness: getValue('clearcoatRoughness', 0.1),
    transparent: getValue('transparent', false),
    opacity: getValue('opacity', 1),
  });
}

export function buildGingivaMaterial(): MeshPhysicalMaterial {
  // const GINGIVA_COLOR = 0xd58686;
  // const GINGIVA_COLOR = 0xedaca7;
  const GINGIVA_COLOR = 0xe8948e;
  return new MeshPhysicalMaterial({
    color: GINGIVA_COLOR,
    emissive: 0x572e32,
    roughness: 0.3,
    metalness: 0,
    ior: 1,
    reflectivity: 0.2,
    iridescence: 0,
    sheen: 0.3,
    sheenRoughness: 0,
    sheenColor: GINGIVA_COLOR,
    specularIntensity: 0.4,
    specularColor: GINGIVA_COLOR,
    clearcoat: 0.4,
    clearcoatRoughness: 0.4,
  });
}

export function buildHdlMaterial(type?: number): MeshPhongMaterial {
  return type && type >= 3 && type <= 7
    ? buildWireMaterial()
    : new MeshPhongMaterial({
        color: 0x00ffff, // Cyan
        shininess: 10.0,
        specular: 0x1c1c1c, // near-black
        flatShading: true,
      });
}

export function buildWireMaterial(): MeshPhongMaterial {
  return new MeshPhongMaterial({
    color: 0xb5b5b5, // Grey
    shininess: 20.0,
    specular: 0x1c1c1c, // near-black
    flatShading: true,
  });
}

export function buildStepNumberMaterial(): MeshPhongMaterial {
  return new MeshPhongMaterial({
    color: 0x996f6b, // dark, pale maroon
    shininess: 10.0,
    specular: 0x1c1c1c,
    flatShading: true,
  });
}

export function buildPointsMaterial(size = 7): PointsMaterial {
  return new PointsMaterial({
    size,
    map: createCanvasMaterial('#000dff', 256),
    transparent: true,
    depthWrite: false,
  });
}

function createCanvasMaterial(color: string, size: number): Texture {
  let matCanvas = document.createElement('canvas');
  matCanvas.width = matCanvas.height = size;
  let matContext = matCanvas.getContext('2d');
  if (!matContext) {
    throw Error('Unable to get 2D context');
  }
  // create texture object from canvas.
  let texture = new Texture(matCanvas);
  // Draw a circle
  let center = size / 2;
  matContext.beginPath();
  matContext.arc(center, center, size / 2, 0, 2 * Math.PI, false);
  matContext.closePath();
  matContext.fillStyle = color;
  matContext.fill();
  // need to set needsUpdate
  texture.needsUpdate = true;
  // return a texture made from the canvas
  return texture;
}
